// sensei-app/client/ui/StudentClassScheduleView.js
import { ComponentBase } from "../../core/ComponentBase.js";
import { RpcDataProvider } from "../../core/RpcDataProvider.js";

export class StudentClassScheduleView extends ComponentBase {
  static styles = `
    .schedule-view-wrapper {
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .schedule-view-filter-bar {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--bs-border-color);
      background-color: var(--bs-tertiary-bg);
      flex-shrink: 0;
    }
    .schedule-accordion-container {
      flex-grow: 1;
      overflow-y: auto;
    }
    .course-accordion-body {
      padding: 0;
    }
    .course-empty-placeholder {
      text-align: center;
      padding: 2rem;
      color: var(--bs-secondary-color);
    }
  `;

  constructor(config) {
    super(config);
    this.courses = [];      // array of { _id, title, topics, progress… }
    this.filters = {};      // { topicType?, status? }
    this.openCourseId = null;    // currently‐open course._id
    this.courseDataViews = new Map(); // courseId → { dataView, eventName, handler }
    this.applyStyles();
  }

  /** Build the outer shell just once */
  async initialize() {
    await super.initialize();

    // outer container
    this.container = document.createElement('div');
    this.container.className = 'schedule-view-wrapper h-100';

    // filter bar
    this.filterBar = this._createFilterBar();

    // accordion container
    this.accordion = document.createElement('div');
    this.accordion.className = 'schedule-accordion-container accordion accordion-flush';
    this.accordion.id = `${this.config.instanceId}-accordion`;

    this.container.append(this.filterBar, this.accordion);

    // translate once
    this.appClient.tx.applyTranslations(this.container);
  }

  /** Called by the layout engine to put us into the DOM */
  render() {
    return this.container;
  }

  /** Refresh the list of enrolled courses (and re-diff the accordion) */
  async refresh() {
    this.log.info("StudentClassScheduleView.refresh()");
    // pick up any ?courseId=… param so we can auto-open
    const { courseId } = this.appClient.layoutEngine.getQueryParams();
    this.openCourseId = courseId || null;

    try {
      const r = await this.appClient.rpcClient.call(
        'StudentCourseRpc.getData',
        { pageSize: 100 }
      );
      this.courses = r.data || [];
      this._syncCourseList();
    } catch (err) {
      this.log.error("Failed to load enrolled courses:", err);
      this.appClient.notifier.error("Could not load course schedules.");
    }
  }

  /** Build the filter bar once, wire up its handlers */
  _createFilterBar() {
    const bar = document.createElement('div');
    bar.className = 'schedule-view-filter-bar row g-2 align-items-center';
    bar.innerHTML = `
      <div class="col-md-3">
        <select class="form-select form-select-sm" data-filter="topicType">
          <option value="">${this.t('All Activity Types')}</option>
          <option value="class">${this.t('Class')}</option>
          <option value="assessment">${this.t('Assessment')}</option>
          <option value="assignment">${this.t('Assignment')}</option>
        </select>
      </div>
      <div class="col-md-3">
        <select class="form-select form-select-sm" data-filter="status">
          <option value="">${this.t('All Statuses')}</option>
          <option value="scheduled">${this.t('Scheduled')}</option>
          <option value="paused">${this.t('Paused')}</option>
          <option value="completed">${this.t('Completed')}</option>
          <option value="missed">${this.t('Missed')}</option>
        </select>
      </div>
      <div class="col-auto">
        <button class="btn btn-sm btn-outline-secondary" id="clear-schedule-filters-btn" title="${this.t('Clear Filters')}">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="col-auto">
        <button class="btn btn-sm btn-outline-primary" id="refresh-schedule-btn" title="${this.t('Refresh All')}">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
    `;

    // cache refs
    this.topicTypeSelect = bar.querySelector('[data-filter=topicType]');
    this.statusSelect = bar.querySelector('[data-filter=status]');

    // wire events
    this.topicTypeSelect.addEventListener('change', () => this._onFilterChange());
    this.statusSelect.addEventListener('change', () => this._onFilterChange());
    bar.querySelector('#clear-schedule-filters-btn')
      .addEventListener('click', () => this._onClearFilters());
    bar.querySelector('#refresh-schedule-btn')
      .addEventListener('click', () => this._onRefreshClick());

    return bar;
  }

  /** User changed one of the selects */
  _onFilterChange() {
    // rebuild our filter object
    this.filters = {};
    if (this.topicTypeSelect.value) this.filters.topicType = this.topicTypeSelect.value;
    if (this.statusSelect.value) this.filters.status = this.statusSelect.value;

    // if a panel is open, reload just that one
    if (this.openCourseId && this.courseDataViews.has(this.openCourseId)) {
      const dv = this.courseDataViews.get(this.openCourseId).dataView;
      dv.dataProvider.loadData({
        filter: { ...this.filters, courseId: this.openCourseId }
      });
    }
  }

  /** Clear filters and re-apply */
  _onClearFilters() {
    this.topicTypeSelect.selectedIndex = 0;
    this.statusSelect.selectedIndex = 0;
    this._onFilterChange();
  }

  /** Refresh click: if a panel is open, reload only its DataView; else full course reload */
  async _onRefreshClick() {
    if (this.openCourseId && this.courseDataViews.has(this.openCourseId)) {
      const dv = this.courseDataViews.get(this.openCourseId).dataView;
      await dv.dataProvider.loadData({
        filter: { ...this.filters, courseId: this.openCourseId }
      });
    } else {
      await this.refresh();
    }
  }

  /** Diff the accordion DOM to match this.courses[] */
  _syncCourseList() {
    // empty placeholder?
    if (!this.courses.length) {
      this.accordion.innerHTML = `
        <div class="course-empty-placeholder">
          <i class="bi bi-journal-x display-4"></i>
          <h5 class="mt-3">${this.t('Not Enrolled In Any Courses')}</h5>
          <p>${this.t('Use "My Coordinator" to find and enroll in courses.')}</p>
        </div>`;
      return;
    }

    // 1) remove any items no longer in the new list
    const newIds = new Set(this.courses.map(c => c._id));
    this.accordion
      .querySelectorAll('[data-course-id]')
      .forEach(el => {
        const id = el.dataset.courseId;
        if (!newIds.has(id)) {
          // destroy its DataView if it was ever opened
          if (this.courseDataViews.has(id)) {
            const { dataView, eventName, handler } = this.courseDataViews.get(id);
            dataView.off(eventName, handler);
            dataView.destroy();
            this.courseDataViews.delete(id);
          }
          el.remove();
        }
      });

    // 2) ensure each course has an accordion-item
    for (const course of this.courses) {
      let item = this.accordion.querySelector(`[data-course-id="${course._id}"]`);
      const collapseId = `collapse-${course._id}`;
      const isOpen = this.openCourseId === course._id;

      if (!item) {
        // first time we saw this course
        item = this._createAccordionItemSkeleton(course, collapseId);
        this.accordion.append(item);
      }
      // update title & progress %
      this._updateAccordionHeader(item, course, isOpen);

      // show or hide via Bootstrap Collapse API
      const collapseEl = item.querySelector(`#${collapseId}`);
      const inst = window.bootstrap.Collapse.getOrCreateInstance(collapseEl);
      if (isOpen) inst.show();
      else inst.hide();
    }
  }

  /** Build a new, empty accordion-item skeleton */
  _createAccordionItemSkeleton(course, collapseId) {
    const div = document.createElement('div');
    div.className = 'accordion-item';
    div.dataset.courseId = course._id;
    div.innerHTML = `
      <h2 class="accordion-header" id="heading-${course._id}">
        <button class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#${collapseId}"
                aria-expanded="false"
                aria-controls="${collapseId}">
          <div style="width:100%">
            <strong class="course-title"></strong>
            <div class="progress mt-2">
              <div class="progress-bar"></div>
            </div>
          </div>
        </button>
      </h2>
      <div id="${collapseId}"
           class="accordion-collapse collapse"
           data-bs-parent="#${this.accordion.id}"
           aria-labelledby="heading-${course._id}">
        <div class="accordion-body course-accordion-body">
          <div class="p-3 text-center text-muted">
            ${this.t('Loading schedule...')}
          </div>
        </div>
      </div>`;

    // wire open/close handlers
    const collapseEl = div.querySelector(`#${collapseId}`);
    collapseEl.addEventListener('shown.bs.collapse', () => this._onPanelOpen(course._id));
    collapseEl.addEventListener('hidden.bs.collapse', () => this._onPanelClose(course._id));

    return div;
  }

  async handleStartActivity(item, dataView) {
    // 1) Skip-ahead check: any earlier scheduled OR paused activity?
    const allSchedules = dataView.dataProvider.data || [];
    const currentIndex = allSchedules.findIndex(s => s._id === item._id);
    const skipped = allSchedules.find((s, idx) =>
      idx < currentIndex && (s.status === 'scheduled' || s.status === 'paused')
    );
    if (skipped) {
      const skipConfirm = await this.appClient.notifier.confirm(
        this.t(
          "You have a previous activity, '{{topicTitle}}', that has not been completed. " +
          "It's recommended to take activities in order. Do you want to skip ahead?",
          { topicTitle: skipped.topicTitle }
        ),
        {
          title: this.t("Skip Ahead?"),
          type: 'warning',
          okButtonText: this.t("Yes, Skip Ahead"),
          cancelButtonText: this.t("No, Go Back")
        }
      );
      if (!skipConfirm) return;
    }

    let route;

    // 2) Completed items
    if (item.status === 'completed') {
      if (item.topicType === 'class') {
        const retakeConfirm = await this.appClient.notifier.confirm(
          this.t(
            "You have already completed this class. Retaking it will count as a new attempt. " +
            "Do you want to proceed?"
          ),
          {
            title: this.t("Retake Class?"),
            type: 'info',
            okButtonText: this.t("Yes, Retake"),
            cancelButtonText: this.t("Cancel")
          }
        );
        if (!retakeConfirm) return;
        route = `/student/classroom?scheduleId=${item._id}`;

      } else if (item.topicType === 'assessment' && item.resultDocumentId) {
        route = `/student/assessment?sessionId=${item.resultDocumentId}&review=true`;

      } else if (item.topicType === 'assignment' && item.resultDocumentId) {
        this.appClient.notifier.info(
          this.t("Assignment review feature is coming soon!")
        );
        return;

      } else {
        this.appClient.notifier.warn(
          this.t("Could not find the results for this activity.")
        );
        return;
      }

    } else {
      // 3) New or paused: confirm when scheduled
      if (item.status === 'scheduled') {
        const startConfirm = await this.appClient.notifier.confirm(
          this.t("Are you sure you want to start this activity?"),
          { title: this.t("Begin Activity") }
        );
        if (!startConfirm) return;
      }

      switch (item.topicType) {
        case 'class':
          route = `/student/classroom?scheduleId=${item._id}`;
          break;
        case 'assessment':
          route = `/student/assessment?scheduleId=${item._id}`;
          break;
        case 'assignment':
          route = `/student/assignment?scheduleId=${item._id}`;
          break;
        default:
          this.appClient.notifier.error(this.t("Unknown activity type."));
          return;
      }
    }

    // finally navigate
    this.appClient.layoutEngine.navigate(route);
  }


  /** Update the title text + progress bar + toggle state */
  _updateAccordionHeader(item, course, isOpen) {
    // compute %
    const total = (course.topics || [])
      .filter(t => t.isMandatory)
      .reduce((s, t) => s + (t.marks || 0), 0);
    const pct = total
      ? Math.round((course.progress?.totalScore || 0) / total * 100)
      : 0;

    item.querySelector('.course-title').textContent = course.title;
    item.querySelector('.progress-bar').style.width = `${pct}%`;

    const btn = item.querySelector('.accordion-button');
    btn.classList.toggle('collapsed', !isOpen);
    btn.setAttribute('aria-expanded', String(!!isOpen));
  }

  async _onPanelOpen(courseId) {
    this.openCourseId = courseId;

    if (!this.courseDataViews.has(courseId)) {
      const item = this.accordion.querySelector(`[data-course-id="${courseId}"]`);
      const bodyEl = item.querySelector('.course-accordion-body');
      bodyEl.innerHTML = ''; // clear “Loading…” placeholder

      const dataView = await this.createManagedComponent('DataView', bodyEl, {
        dataProvider: new RpcDataProvider({
          appClient: this.appClient,
          rpcClass: "StudentRpc",
          autoLoad: true,
          filter: { ...this.filters, courseId }
        }),
        title: `Schedule for ${item.querySelector('.course-title').textContent}`,
        toolbar: { enabled: false },
        viewMode: 'list',
        fullHeight: false,
        fields: [
          { name: 'topicTitle', label: 'Activity' },
          {
            name: 'topicType', label: 'Type',
            render: v => `<span class="badge text-bg-info text-capitalize">${this.t(v)}</span>`
          },
          {
            name: 'scheduledDateTime', label: 'Time',
            render: v => new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
          },
          {
            name: 'status', label: 'Status',
            render: v => `<span class="badge text-bg-${this._statusClass(v)} text-capitalize">${this.t(v)}</span>`
          },
          {
            name: 'attendanceCount', label: 'Attempts',
            render: v => v > 0
              ? `<span class="badge rounded-pill text-bg-secondary">${v}</span>`
              : this.t('Not started')
          }
        ],
        actions: [
          // scheduled
          { label: 'Start Class', icon: 'bi-camera-video-fill', eventName: 'startActivity', visibleWhen: i => i.status === 'scheduled' && i.topicType === 'class' },
          { label: 'Start Assessment', icon: 'bi-clipboard2-check-fill', eventName: 'startActivity', visibleWhen: i => i.status === 'scheduled' && i.topicType === 'assessment' },
          { label: 'Start Assignment', icon: 'bi-file-earmark-arrow-up-fill', eventName: 'startActivity', visibleWhen: i => i.status === 'scheduled' && i.topicType === 'assignment' },
          // paused
          { label: 'Resume', icon: 'bi-play-circle-fill', eventName: 'startActivity', visibleWhen: i => i.status === 'paused' },
          // completed non-class (review)
          { label: 'Review', icon: 'bi-book-half', className: 'btn-info btn-sm', eventName: 'startActivity', visibleWhen: i => i.status === 'completed' && i.topicType !== 'class' },
          // completed class → two separate buttons
          { label: 'Review Class', icon: 'bi-book-half', className: 'btn-info btn-sm', eventName: 'reviewActivity', visibleWhen: i => i.status === 'completed' && i.topicType === 'class' },
          { label: 'Retake Class', icon: 'bi-arrow-repeat', className: 'btn-outline-primary btn-sm', eventName: 'startActivity', visibleWhen: i => i.status === 'completed' && i.topicType === 'class' }
        ]
      });

      // wire startActivity
      const evStart = `startActivity`;
      const handlerStart = it => this.handleStartActivity(it, dataView);
      dataView.on(evStart, handlerStart);

      // wire reviewActivity
      const evReview = `reviewActivity`;
      const handlerReview = () =>
        this.appClient.notifier.info(
          this.t("Class review feature is coming soon!")
        );
      dataView.on(evReview, handlerReview);

      // store both handlers
      this.courseDataViews.set(courseId, {
        dataView,
        eventHandlers: [
          { eventName: evStart, handler: handlerStart },
          { eventName: evReview, handler: handlerReview }
        ]
      });

    } else {
      // already exists → just reapply filters & reload
      const { dataView } = this.courseDataViews.get(courseId);
      dataView.dataProvider.loadData({ filter: { ...this.filters, courseId } });
    }
  }
  /** Panel closed: forget the openCourseId (but leave the DataView cached) */
  _onPanelClose(courseId) {
    if (this.openCourseId === courseId) {
      this.openCourseId = null;
    }
  }

  /** Map statuses → badge classes */
  _statusClass(s) {
    const m = {
      scheduled: 'primary',
      completed: 'success',
      cancelled: 'secondary',
      missed: 'danger',
      paused: 'info'
    };
    return m[s] || 'light';
  }

  destroy() {
    this.courseDataViews.forEach(({ dataView, eventHandlers }) => {
      for (const { eventName, handler } of eventHandlers) {
        dataView.off(eventName, handler);
      }
      dataView.destroy();
    });
    this.courseDataViews.clear();
    super.destroy();
  }
}